import type { ReactNode } from 'react'
import type { ToastData, ToastViewportProps } from './types'

import { Toast as BaseToast } from '@base-ui/react/toast'

import { AnimatedButton } from '#components/button'
import { IconErrorDiamond } from '#components/icon/IconErrorDiamond'
import { IconInfoCircle } from '#components/icon/IconInfoCircle'
import { IconSuccessCircle } from '#components/icon/IconSuccessCircle'
import { cn } from '#lib/cn'

import { iconContainerVariants, viewportVariants } from './styles'
import { ToastContent } from './toast-content'
import { ToastDescription } from './toast-description'
import { ToastDismissButton } from './toast-dismiss-button'
import { ToastRoot } from './toast-root'
import { ToastTitle } from './toast-title'
import { ToastPosition, type ToastVariant } from './types'
import { resolveVariant } from './utils'

const presetIcons: Record<ToastVariant, ReactNode> = {
  informational: <IconInfoCircle />,
  success: <IconSuccessCircle />,
  error: <IconErrorDiamond />,
}

function IconContainer({ variant }: { variant: ToastVariant }) {
  return (
    <div className={cn(iconContainerVariants({ variant }))}>
      <span className="[&>svg]:size-4">{presetIcons[variant]}</span>
    </div>
  )
}

export function ToastViewport({
  className,
  position = ToastPosition.BottomRight,
  ...props
}: ToastViewportProps) {
  const { toasts } = BaseToast.useToastManager<ToastData>()

  return (
    <BaseToast.Viewport
      className={cn(viewportVariants({ position }), className)}
      {...props}
    >
      {toasts.map((toast) => (
        <ToastRoot key={toast.id} toast={toast} position={position}>
          <ToastContent>
            <IconContainer variant={resolveVariant(toast)} />
            {toast.description || toast.data?.action ? (
              <div className="flex flex-col gap-2.25 flex-1 min-w-0 pt-0.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex h-6 items-center flex-1 min-w-0">
                    <ToastTitle>{toast.title}</ToastTitle>
                  </div>
                  {toast.description && (
                    <ToastDescription>{toast.description}</ToastDescription>
                  )}
                </div>
                {toast.data?.action && (
                  <AnimatedButton
                    animateWidth
                    size="small"
                    variant="secondary"
                    onClick={toast.data.action.onClick}
                    isLoading={toast.data.action.isLoading}
                    className="w-min"
                  >
                    {toast.data.action.label}
                  </AnimatedButton>
                )}
              </div>
            ) : (
              <div className="flex h-6 items-center flex-1 min-w-0">
                <ToastTitle>{toast.title}</ToastTitle>
              </div>
            )}
            <ToastDismissButton />
          </ToastContent>
        </ToastRoot>
      ))}
    </BaseToast.Viewport>
  )
}
ToastViewport.displayName = 'Toast.Viewport'
